/**
 * @file CrossfitSenior.tsx
 * @summary Article de blog — CrossfitSenior
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, Heart, ShieldCheck, Activity, Users } from "lucide-react";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/crossfit-senior";

const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const faq = [
  { "@type": "Question", name: "Peut-on commencer le CrossFit après 60 ans ?", acceptedAnswer: { "@type": "Answer", text: "Oui. Les séances sont adaptées au niveau physique, à la mobilité et aux capacités de chacun. L'objectif est de progresser durablement, pas de suivre le rythme des autres." } },
  { "@type": "Question", name: "Faut-il un certificat médical ?", acceptedAnswer: { "@type": "Answer", text: "Pour une pratique régulière après 60 ans, un avis médical favorable est recommandé. En cas de pathologie chronique, l'accord de votre médecin est indispensable avant toute reprise." } },
  { "@type": "Question", name: "Et si je n'ai jamais fait de sport ?", acceptedAnswer: { "@type": "Answer", text: "Aucun problème. Beaucoup de seniors qui rejoignent CrossFit Chistera n'avaient pas pratiqué depuis des années. Les coachs commencent par les fondamentaux : posture, respiration, mouvements simples." } },
  { "@type": "Question", name: "Le CrossFit est-il dangereux pour les articulations ?", acceptedAnswer: { "@type": "Answer", text: "Non, à condition d'être bien encadré. Charges, amplitudes et impacts sont adaptés. Le renforcement musculaire protège justement les articulations sur le long terme." } },
];

const jsonLd = [
  { "@context": "https://schema.org", "@type": "Article", headline: "CrossFit après 60 ans : reprendre le sport en confiance", description: "Découvrez comment les seniors reprennent confiance et retrouvent la forme chez CrossFit Chistera grâce à un accompagnement adapté.", author: { "@type": "Organization", name: "CrossFit Chistera" }, publisher: { "@type": "Organization", name: "CrossFit Chistera" }, mainEntityOfPage: URL, inLanguage: "fr-FR" },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

const benefices = [
  { icon: Activity, title: "Énergie au quotidien", desc: "Monter les escaliers, porter les courses, jouer avec les petits-enfants." },
  { icon: ShieldCheck, title: "Os et articulations", desc: "Le renforcement musculaire protège la densité osseuse et la mobilité." },
  { icon: Heart, title: "Cœur et souffle", desc: "Cardio doux, progressif, mesuré — adapté à votre rythme." },
  { icon: Users, title: "Lien social", desc: "Une communauté bienveillante, sans jugement, qui encourage." },
];

export default function CrossfitSenior() {
  return (
    <>
      <SEO
        title="CrossFit après 60 ans : reprendre le sport en confiance"
        description="Découvrez comment les seniors reprennent confiance et retrouvent la forme chez CrossFit Chistera grâce à un accompagnement adapté."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">CrossFit après 60 ans</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">Sport santé · Lecture ~ 6 min</span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">CrossFit après 60 ans : « Je voulais juste réussir à monter les escaliers sans souffler »</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                Quand on parle de CrossFit, beaucoup imaginent immédiatement des athlètes qui soulèvent des charges impressionnantes. Pourtant, à <strong>CrossFit Chistera</strong>, certaines des plus belles victoires ressemblent plutôt à ça : retrouver de l'énergie le matin, porter ses courses sans douleur, ou réussir à jouer avec ses petits-enfants sans avoir besoin de « récupérer » ensuite.
              </p>
              <BlogCTA variant="hero" primaryLabel="Tester un cours gratuitement" />
            </header>

            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2">Témoignage</p>
              <p className="text-foreground/90 leading-relaxed italic">
                « Je pensais que ce n'était pas fait pour moi. Le coach m'a surtout demandé comment je me sentais physiquement. Ça m'a rassuré immédiatement. » — <strong className="not-italic">Jean-Marc, 64 ans</strong>
              </p>
            </aside>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>« Au début, je regardais surtout les autres »</h2>
              <p>
                Jean-Marc, 64 ans, ancien commercial, raconte en souriant sa première séance. Il s'attendait à devoir courir partout, à enchaîner des mouvements impossibles. La réalité a été tout autre : un échauffement guidé, des explications claires, et un coach attentif à chaque détail.
              </p>
              <p>
                À CrossFit Chistera, les séances sont adaptées au niveau, à l'âge et aux capacités de chacun. <strong>Un mouvement peut être simplifié. Une charge peut être réduite. Un exercice peut être remplacé.</strong> L'objectif n'est pas de suivre le rythme des autres — c'est de progresser durablement, sans se blesser.
              </p>

              <h2>Pourquoi le CrossFit est particulièrement utile après 60 ans</h2>
              <p>
                Après 60 ans, le corps perd naturellement de la masse musculaire (on parle de sarcopénie), de la densité osseuse, de la mobilité articulaire et de l'équilibre. C'est précisément ce que le CrossFit, bien encadré, permet de préserver — et souvent de regagner.
              </p>
            </div>

            <section className="my-12">
              <div className="grid md:grid-cols-2 gap-4">
                {benefices.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.title} className="border border-border bg-card rounded-lg p-5 hover:border-gold/50 transition-colors">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                        <h3 className="font-heading text-lg text-foreground">{b.title}</h3>
                      </div>
                      <p className="text-foreground/80 text-sm font-body normal-case">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

            <div className="my-16 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">2 séances d'essai gratuites</h2>
              <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Venez tester à votre rythme, échangez avec un coach, sans engagement.</p>
              <BlogCTA />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>Une box familiale dans l'Est lyonnais</h2>
              <p>
                CrossFit Chistera est une <strong>salle de CrossFit familiale</strong> située à Saint-Pierre-de-Chandieu, à proximité de Mions, Heyrieux, Saint-Bonnet-de-Mure et Toussieu. Pour beaucoup de seniors de l'Est lyonnais et du Nord-Isère, c'est devenu un repère pour la <strong>remise en forme</strong> et le sport santé.
              </p>
              <p>
                Cours en petit comité, coachs diplômés (BPJEPS, STAPS, CrossFit Level 1), créneaux en journée adaptés aux retraités : tout est pensé pour que l'expérience soit progressive et durable.
              </p>
            </div>

            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">Questions fréquentes</h2>
              <div className="space-y-4">
                {faq.map((q) => (
                  <details key={q.name} className="group border border-border bg-card rounded-lg p-5 open:border-gold/50">
                    <summary className="cursor-pointer font-heading text-lg text-foreground flex justify-between items-center gap-4 list-none">
                      <span>{q.name}</span>
                      <ChevronRight className="w-5 h-5 text-gold shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-foreground/85 leading-relaxed font-body normal-case">{q.acceptedAnswer.text}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="my-16">
              <div className="border border-gold/30 bg-gradient-to-br from-charcoal/60 to-charcoal/20 rounded-lg px-6 py-10 text-center">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Reprenez le sport en confiance</h2>
                <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Réservez votre séance d'essai gratuite ou échangez d'abord avec un coach.</p>
                <BlogCTA />
              </div>
            </section>

            <section className="my-12 border-t border-border pt-8">
              <h2 className="text-xl font-heading text-foreground mb-4">À lire aussi</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-body normal-case">
                <li><Link to="/reprendre-le-sport" className="text-gold hover:underline">→ Reprendre le sport</Link></li>
                <li><Link to="/crossfit-debutant" className="text-gold hover:underline">→ CrossFit débutant</Link></li>
                <li><Link to="/cours/wod" className="text-gold hover:underline">→ Le cours WOD</Link></li>
                <li><Link to="/blog/quest-ce-que-le-crossfit" className="text-gold hover:underline">→ C'est quoi le CrossFit ?</Link></li>
              </ul>
            </section>
          </article>
        </main>
        <Footer />
        <BlogStickyCTA label="Tester un cours gratuitement" />
      </div>
    </>
  );
}
